import React, { useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';

// Hooks
import { useGuide } from '@/hooks/useGuides';
import { useLanguage } from '../contexts/LanguageContext';

// Types
import { GuideDifficulty, GuideLanguage } from '../services/apis/guides';
import { JobStatus } from '../types';

// Components
import GuideBuilderSkeleton from '../components/guide-builder/GuideBuilderSkeleton';
import GuideBuilderNotFound from '../components/guide-builder/GuideBuilderNotFound';
import GuideBuilderError from '../components/guide-builder/GuideBuilderError';
import GuideBuilderEditor from '../components/guide-builder/GuideBuilderEditor';
import GuideBuilderErrorBoundary from '../components/guide-builder/GuideBuilderErrorBoundary';
import GuideJobPending from '../components/guide-builder/GuideJobPending';
import GuideJobFailed from '../components/guide-builder/GuideJobFailed';

// Context
import { GuideBuilderProvider } from '../contexts/GuideBuilderContext';

// Schemas and types
import { GuideBuilderFormData } from '../components/guide-builder/schemas';
import { TransformedStep } from '@/components/guide-builder/schemas';

const GuideBuilder: React.FC = () => {
  return (
    <GuideBuilderErrorBoundary>
      <GuideBuilderContent />
    </GuideBuilderErrorBoundary>
  );
};

const GuideBuilderContent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentLanguage } = useLanguage();

  // Determine goBackPath based on route - if accessed from admin, go back to admin guides page
  const goBackPath = '/admin/guides';

  // Check if we should poll based on job status
  // We need to check this before the hook call, but we'll use a function for refetchInterval
  const {
    data: guideResponse,
    isLoading: isGuideLoading,
    error: guideError,
    refetch,
  } = useGuide({
    id: id || '',
    shouldSkip: !id,
    refetchInterval: (query) => {
      // Check if job is pending and poll every 2 seconds
      // Access the data from the query state - it's ApiResponse<Guide>
      const responseData = query.state.data;
      if (!responseData?.data) {
        return false;
      }
      const job = responseData.data.job;
      if (job && (job.status === JobStatus.PENDING || job.status === JobStatus.PROCESSING)) {
        return 5000; // Poll every 5 seconds
      }
      return false; // Stop polling
    },
  });

  const [selectedStep, setSelectedStep] = useState<TransformedStep | undefined>(undefined);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const handleSelectStep = (step: TransformedStep | undefined) => {
    setSelectedStep(step);
  };
  const handlePreviewToggle = () => {
    setIsPreviewMode((prev) => !prev);
  };

  // Check if job is pending or failed (exclude MANUALLY_RESOLVED)
  const job = guideResponse?.data?.job;
  const isJobPending = job && (job.status === JobStatus.PENDING || job.status === JobStatus.PROCESSING);
  const isJobFailed = job && job.status === JobStatus.FAILED;

  const actions = {
    onSelectStep: handleSelectStep,
    onPreviewToggle: handlePreviewToggle,
    setIsPublishing,
    setIsTranslating,
  };

  const state = {
    selectedStep,
    isPreviewMode,
    isPublishing,
    isTranslating,
  };

  const guide: GuideBuilderFormData = useMemo(() => {
    const defaultValues = {
      title: 'Guide Title',
      description: '',
      difficulty: GuideDifficulty.BEGINNER,
      estimatedTime: undefined,
      steps: [],
      selectedStepId: undefined,
      categoryId: undefined,
      productId: undefined,
      language: currentLanguage as GuideLanguage,
    };
    if (!guideResponse?.data) return defaultValues;
    const guideMetadata =
      guideResponse.data.translations.find((t) => t.language === currentLanguage) || guideResponse.data.translations[0];
    const steps = guideResponse.data.steps.map((s) => {
      const stepTranslation = s.translations.find((t) => t.language === currentLanguage) || s.translations[0];
      return {
        id: s.id,
        title: stepTranslation.title || '',
        content: stepTranslation.content || {},
        type: s.type,
        position: s.position,
        glossaries: s.glossaries,
        questionConfig: s.questionConfig, // Include questionConfig from step level
      };
    });
    return {
      ...defaultValues,
      id: guideResponse.data.id,
      difficulty: guideResponse.data.difficulty,
      categoryId: guideResponse.data.category?.id,
      productId: guideResponse.data.product?.id,
      title: guideMetadata.title || '',
      description: guideMetadata.description || '',
      isPublished: guideResponse.data.isPublished,
      isRecommended: guideResponse.data.isRecommended || false,
      estimatedTime: guideResponse.data.estimatedTime,
      steps: steps,
    };
  }, [guideResponse, currentLanguage]);

  if (isGuideLoading) {
    return <GuideBuilderSkeleton />;
  }

  if (!guideResponse?.data) {
    return <GuideBuilderNotFound />;
  }

  if (guideError) {
    return <GuideBuilderError onRetry={() => refetch()} />;
  }

  // Show pending component if job is pending
  if (isJobPending) {
    return <GuideJobPending status={job?.status} />;
  }

  // Show failed component if job failed
  if (isJobFailed) {
    return <GuideJobFailed jobId={job.id} guideId={guideResponse.data.id} errorMessage={job?.errorMessage} />;
  }

  // Main editor view with context provider
  return (
    <GuideBuilderProvider guide={guide} actions={actions} state={state} goBackPath={goBackPath}>
      <GuideBuilderEditor />
    </GuideBuilderProvider>
  );
};

export default GuideBuilder;
